// The reviewer's half of the occasion system. src/lib/seasonal.ts decides what
// an occasion IS and what is worth proposing; this decides what a person can
// confirm and writes the answer to lyh_events.occasions.
//
// The rule the whole design turns on: a keyword PROPOSES, a person CONFIRMS.
// Nothing here ever writes a tag that was not clicked. A proposal renders
// unselected, and an event approved without anyone touching it keeps the column
// default of '{}' and belongs to no occasion.
//
// Two universes, deliberately different sizes:
//   proposals  keyword matched AND the event falls inside the occasion's run-up
//   offerable  the event falls inside the run-up, whatever it is called
// So "Winter Wonderland" on 20 December proposes nothing but can still be tagged
// Christmas by hand, while nothing in July can be tagged Christmas at all. The
// window is what makes the manual escape hatch safe to leave open.

import { useCallback, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { ALL_OCCASIONS, proposeOccasions, seasonalFilters } from './seasonal'
import { PUBLIC_EVENT_COLUMNS } from './eventColumns'
import type { EventRow } from '../types'

/** lyh_events rows carry `occasions` (not null, default '{}'); EventRow predates
    the column and is owned elsewhere, so it is widened here the same way
    seasonal.ts does it. */
export type TaggedEventRow = EventRow & { occasions: string[] | null }

// ALL_OCCASIONS, not just SEASONAL: a confirmed tag can be a national day
// (Pi Day, National Donut Day) just as easily as a holiday, and this has to
// find its label either way.
export function occasionLabel(slug: string): string {
  return ALL_OCCASIONS.find((s) => s.slug === slug)?.label ?? slug
}

export function eventOccasions(e: TaggedEventRow): string[] {
  return Array.isArray(e.occasions) ? e.occasions : []
}

export interface OccasionOption {
  slug: string
  label: string
  /** The occurrence this event sits in the run-up to, so the picker can show it
      and a reviewer can see they are about to tag the right year's Christmas. */
  date: Date
}

/** Everything this event's DATE makes it eligible for, named or not. */
export function occasionOptions(e: EventRow): OccasionOption[] {
  const when = new Date(e.start_time)
  if (Number.isNaN(when.getTime())) return []
  return seasonalFilters(when).map((s) => ({
    slug: s.slug,
    label: s.label,
    date: s.date,
  }))
}

/**
 * Write the confirmed set, and prove it landed.
 *
 * The read-back is not belt and braces. An UPDATE that RLS refuses is a no-op,
 * NOT an error: PostgREST answers 204 with zero rows affected and supabase-js
 * hands back a null error. Verified against the live table on 2026-07-28, an
 * anon PATCH of `occasions` returned http 204 and the column stayed empty. So a
 * null error would have let the UI show a tag that was never saved, which is the
 * one failure a reviewer could not see. The returned row is the proof.
 */
export async function saveEventPatch(
  id: string,
  patch: Record<string, unknown>,
): Promise<{ error: string | null }> {
  if (Object.keys(patch).length === 0) return { error: null }

  const { data, error } = await supabase
    .from('lyh_events')
    .update(patch)
    .eq('id', id)
    .select('id')

  if (error) return { error: error.message }
  if (!data || data.length === 0) {
    return { error: 'Nothing was updated. Check you are still signed in.' }
  }
  return { error: null }
}

export async function saveOccasions(
  id: string,
  slugs: string[],
): Promise<{ error: string | null }> {
  return saveEventPatch(id, { occasions: slugs })
}

/** True when this event has something a reviewer would want to look at now:
    a tag already, or a keyword proposal. Measured against the live table on
    2026-07-28 this was 0 of 188 upcoming events, which is the honest answer, not
    a bug: no occasion's run-up overlapped the three weeks the calendar holds. */
export function isHighlighted(e: TaggedEventRow): boolean {
  return eventOccasions(e).length > 0 || proposeOccasions(e).length > 0
}

/**
 * Already-approved upcoming events that could carry an occasion: anything inside
 * some occasion's run-up, plus anything already tagged so a wrong tag can always
 * be taken off again.
 *
 * Returning the whole in-window set rather than just the proposals is
 * deliberate, and the numbers are why. On 2026-07-28 the live table had 117
 * upcoming events inside the First Friday window and NONE of them matched its
 * keyword. Listing only proposals would have shown an empty panel while leaving
 * the one art walk in there untaggable; listing all 117 would be a wall of
 * Preschool Play Group. So the caller shows the highlighted ones and offers
 * search across the rest.
 */
export function useTaggableEvents(enabled: boolean) {
  const [taggable, setTaggable] = useState<TaggedEventRow[]>([])

  const refetch = useCallback(async () => {
    if (!enabled) {
      setTaggable([])
      return
    }
    const { data, error } = await supabase
      .from('lyh_events')
      .select(PUBLIC_EVENT_COLUMNS)
      .eq('status', 'approved')
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true })
    if (error) {
      console.error('load taggable events', error)
      setTaggable([])
      return
    }
    const rows = (data as TaggedEventRow[]) ?? []
    setTaggable(
      rows.filter((e) => occasionOptions(e).length > 0 || eventOccasions(e).length > 0),
    )
  }, [enabled])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { taggable, refetch }
}
